/* eslint-disable no-unused-vars */
import React, { useState, useEffect, useMemo, useImperativeHandle, forwardRef } from 'react';
import { useParams } from 'react-router-dom';
import useFetchList from 'hooks/useFetchList';
import { useSelector } from 'react-redux';
import { NULL_QUERY_DELAY } from '../../Shared/constants';
import { GET_REPORTABLES_COLUMNS } from '../constants';
import { CLAIM_REPORTS_STATUSES } from '../../ClaimReport/constants';
import QhsTable from '../../Shared/Table/Table';
import { convertDataFromApi, useGenerateColumns } from '../../Shared/Table/helpers';
import TableActions from './widgets/TableActions/TableActions';
import { TABLE_KEYS } from '../../../redux_store/reducer/reducers/tableReducer/tableReducer';

/*
  could be rendered in 2 cases:
  - all account reportables
  - reportables for a special user (patientUuid in URL params)
*/

const ReportablesList = forwardRef(({
  setFilterStyle,
  setTotalCount,
  tableTitle,
  q,
  columnList,
}, ref) => {
  const { accountUuid: paramAccountUuid } = useParams();
  const {
    metadata: {
      currentAccount,
    },
    isDistributionSponsor,
  } = useSelector((state) => state.user);
  const [modifiedReportables, setModifiedReportables] = useState();
  const tableKey = isDistributionSponsor ? TABLE_KEYS.reportableDSTable : TABLE_KEYS.reportableTable;
  const columns = useGenerateColumns(columnList || GET_REPORTABLES_COLUMNS, tableKey, {}, {}).columns;
  const fields = columns.map((columnData) => columnData.accessorKey).join();

  const baseEndpoint = `/accounts/${paramAccountUuid || currentAccount.uuid
    }/reportables`;
  const {
    items,
    totalAccountsCount,
    totalCount,
    completionRate,
    aggregations,
    reloadData,
    clearFilter,
    sort,
    onSort,
    filter,
    onFilter,
    loading: dataLoading,
    onLoadMoreRows,
  } = useFetchList({
    baseEndpoint,
    baseParams: {
      fields: fields,
      q: q || undefined,
    },
    baseFilter: [{ id: 'reportStatus', value: [CLAIM_REPORTS_STATUSES.reportable] }],
    tableKey,
    canLoad: fields.length,
  });

  const [prevTotalCount, setPrevTotalCount] = useState(totalCount);

  useEffect(() => {
    if (items) {
      const newModifiedItems = convertDataFromApi(items, tableKey, filter);
      setModifiedReportables(newModifiedItems);
    }
  }, [items]);

  useEffect(() => {
    if (prevTotalCount !== totalCount) {
      setFilterStyle(!!filter?.reduce((accumulator, { value }) => {
        return accumulator + value.toString().replaceAll(' ', '').length;
      }, 0));
      setPrevTotalCount(totalCount);
      setTotalCount && setTotalCount(totalCount);
    }
  }, [filter, totalCount]);

  const reloadReportableList = (hasFilter) => {
    if (!hasFilter) {
      clearFilter();
    } else {
      reloadData({ delay: NULL_QUERY_DELAY });
    }
  };

  useImperativeHandle(ref, () => ({
    reloadList(v) {
      reloadReportableList(v);
    },
  }));

  return <QhsTable
    tableKey={tableKey}
    columns={columns}
    data={modifiedReportables || []}
    fetchNextPage={onLoadMoreRows}
    isFetching={dataLoading}
    isLoading={dataLoading && !modifiedReportables}
    totalRowCount={totalCount}
    sorting={sort}
    setSorting={onSort}
    filter={filter}
    setFilters={onFilter}
    tableTitle={tableTitle}
    showRefreshTableButton
    reloadData={reloadReportableList}
    menuActions={<TableActions
      totalCount={totalCount}
      totalAccountsCount={totalAccountsCount}
      filter={filter}
      sort={sort}
      accountUuid={items?.length ? items[0]?.accountUUID : null}
      accountBusinessName={items?.length ? items[0]?.accountBusinessName : null}
      reloadData={reloadReportableList}
      aggregations={aggregations}
    />}
  />;
});

export default ReportablesList;

